import {Inject, Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {SuppliersService} from '../../../services/suppliers.service';
import {DeliveriesActions} from './deliveries.index';
import {catchError, exhaustMap, map, switchMap, tap} from 'rxjs/operators';
import {
  AnalyticsService,
  CoreService,
  DocumentService,
  ImageService,
  Param,
  ProleisObject,
  Types
} from '@proleis/proleis-rest-client';
import {forkJoin, Observable, of} from 'rxjs';
import {Options} from 'highcharts';
import {APP_GUIDS, AppGuids} from '../../../app.guids';
import {LoadingService, NotificationService} from '@proleis/proleis-web-app';
import {FileResponse} from '../../../models/file.response';
import {saveAs} from 'file-saver';

@Injectable()
export class DeliveriesEffects {

  constructor(private actions: Actions, private supplierService: SuppliersService,
              private coreService: CoreService, private notificationService: NotificationService,
              private analyticsService: AnalyticsService, @Inject(APP_GUIDS) private guids: AppGuids,
              private imageService: ImageService, private ls: LoadingService,
              private documentsService: DocumentService) {
  }

  loadPlannedDeliveries$ = createEffect(() => {
    const aliasFields = 'PBO_DELIVERY_NAME=PBO_DELIVERY_ID.NAME;FREIGABE_GUID=PBO_DELIVERY_ID.FREIGABE_ID.GUID;' +
      'FREIGABE_ICON=PBO_DELIVERY_ID.FREIGABE_ID.CLIENTICON;FREIGABE_NAME=PBO_DELIVERY_ID.FREIGABE_ID.TEXT;' +
      'CONFIRMED=PBO_DELIVERY_ID.COUNT_DELIVERED;PBOSUBTYPENAME=PBO_SUBTYP_ID.DESCRIPTION;PBOSUBTYPEICON=PBO_SUBTYP_ID.CLIENTICON;' +
      'INCIDENT=LINK_VORFALL_ID.NAME;PBOSUBTYPEGUID=PBO_SUBTYP_ID.GUID';
    return this.actions.pipe(
      ofType(DeliveriesActions.LOAD_PLANNED_DELIVERIES),
      exhaustMap(action => {
        return this.supplierService.getPlannedDeliveries(action.orderId, action.posId, aliasFields).pipe(
          map(deliveries => DeliveriesActions.LOAD_PLANNED_DELIVERIES_SUCCESS({deliveries: this.sortDeliveries(deliveries)}))
        );
      })
    );
  });

  loadPlannedDeliveriesSuccess$ = createEffect(() => {
    return this.actions.pipe(
      ofType(DeliveriesActions.LOAD_PLANNED_DELIVERIES_SUCCESS),
      map(action => DeliveriesActions.LOAD_DELIVERY_ICONS({deliveries: action.deliveries}))
    );
  });

  loadPlannedDeliveriesError$ = createEffect(() => {
    return this.actions.pipe(
      ofType(DeliveriesActions.LOAD_PLANNED_DELIVERIES_ERROR)
    );
  }, {dispatch: false});

  createPlannedDelivery$ = createEffect(() => {
    return this.actions.pipe(
      ofType(DeliveriesActions.CREATE_PLANNED_DELIVERY),
      exhaustMap(action => {
        return this.supplierService.createPlannedDelivery(
          action.pboId, action.posId, action.subtypeGuid, action.plannedDate, action.plannedCount, action.deliveryComment,
          action.deliveryNoteNumber, action.incidentId
        ).pipe(
          map(result => DeliveriesActions.CREATE_PLANNED_DELIVERY_SUCCESS({posId: action.posId, orderId: action.pboId}))
        );
      })
    );
  });

  createPlannedDeliverySuccess$ = createEffect(() => {
    return this.actions.pipe(
      ofType(DeliveriesActions.CREATE_PLANNED_DELIVERY_SUCCESS),
      map(action => DeliveriesActions.LOAD_PLANNED_DELIVERIES({orderId: action.orderId, posId: action.posId}))
    );
  });

  createPlannedDeliveryError$ = createEffect(() => {
    return this.actions.pipe(
      ofType(DeliveriesActions.CREATE_PLANNED_DELIVERY_ERROR)
    );
  }, {dispatch: false});

  updatePlannedDelivery$ = createEffect(() => {
    return this.actions.pipe(
      ofType(DeliveriesActions.UPDATE_PLANNED_DELIVERY),
      exhaustMap(action => {
        return this.supplierService.updatePlannedDelivery(
          action.orderId, action.posId, action.deliveryId, action.plannedCount, action.plannedDate, action.deliveryComment, action.incidentId
        ).pipe(
          map(() => DeliveriesActions.UPDATE_PLANNED_DELIVERY_SUCCESS({orderId: action.orderId, posId: action.posId}))
        );
      })
    );
  });

  updatePlannedDeliverySuccess$ = createEffect(() => {
    return this.actions.pipe(
      ofType(DeliveriesActions.UPDATE_PLANNED_DELIVERY_SUCCESS),
      map(action => DeliveriesActions.LOAD_PLANNED_DELIVERIES({orderId: action.orderId, posId: action.posId}))
    );
  });

  updatePlannedDeliveryError$ = createEffect(() => {
    return this.actions.pipe(
      ofType(DeliveriesActions.UPDATE_PLANNED_DELIVERY_ERROR)
    );
  }, {dispatch: false});

  deleteDelivery$ = createEffect(() => {
    return this.actions.pipe(
      ofType(DeliveriesActions.DELETE_PLANNED_DELIVERY),
      exhaustMap(action => {
        return this.supplierService.deletePlannedDelivery(action.orderId, action.posId, action.deliveryId).pipe(
          map(result => DeliveriesActions.DELETE_PLANNED_DELIVERY_SUCCESS({
            orderId: action.orderId,
            posId: action.posId,
            deliveryId: action.deliveryId
          }))
        );
      })
    );
  });

  deleteDeliverySuccess$ = createEffect(() => {
    return this.actions.pipe(
      ofType(DeliveriesActions.DELETE_PLANNED_DELIVERY_SUCCESS),
      map(action => DeliveriesActions.LOAD_PLANNED_DELIVERIES({
        orderId: action.orderId,
        posId: action.posId
      }))
    );
  });

  cancelDelivery$ = createEffect(() => {
    return this.actions.pipe(
      ofType(DeliveriesActions.CANCEL_DELIVERY),
      exhaustMap(action => {
        return this.coreService.executeAction(action.deliveryNoteId, '§3e1ad372-2c90-11b2-8008-9dbbf888e821', null).pipe(
          map(result => DeliveriesActions.CANCEL_DELIVERY_SUCCESS({
            posId: action.pseId,
            orderId: action.orderId
          }))
        );
      })
    );
  });

  cancelDeliverySuccess$ = createEffect(() => {
    return this.actions.pipe(
      ofType(DeliveriesActions.CANCEL_DELIVERY_SUCCESS),
      tap(result => this.notificationService.showNotification('Lieferschein storniert')),
      map(action => {
        return DeliveriesActions.LOAD_PLANNED_DELIVERIES({
          orderId: action.orderId,
          posId: action.posId
        });
      })
    );
  });

  cancelDeliveryError$ = createEffect(() => {
    return this.actions.pipe(
      ofType(DeliveriesActions.CANCEL_DELIVERY_ERROR)
    );
  }, {dispatch: false});

  createDeliveryNote$ = createEffect(() => {
    return this.actions.pipe(
      ofType(DeliveriesActions.CREATE_DELIVERYNOTE),
      exhaustMap(action => {
        this.ls.setBlocking('deliveryNote', true, 'Erstelle Lieferschein, dies kann einige Sekunden dauern.');
        const params: Param[] = [
          {name: 'STEP_DELIVERY_DATE', value: action.deliveryDate, type: Types.DATE},
          {name: 'STEP_DELIVERY_NOTE_NUMBER', value: action.deliveryNoteNumber, type: Types.TEXT},
          {name: 'STEP_DELIVERY_COUNT', value: action.deliveryCount, type: Types.DOUBLE},
          {name: 'STEP_DELIVERY_COMMENT', value: action.deliveryComment, type: Types.TEXT},
          {name: 'PLANNED_DELIVERY_ID', value: action.deliveryId, type: Types.DOUBLE}
        ];
        return this.coreService.executeAction<any>(
          action.posId, '§' + this.guids.GUID_SCRIPT_PSE_TL_EXTERN_SUPPLIER_CREATE_PBO_DOCUMENT, params
        ).pipe(
          switchMap(result => [
            DeliveriesActions.CREATE_DELIVERYNOTE_SUCCESS({
              deliveryNote: result, posId: action.posId, orderId: action.orderId
            }),
            DeliveriesActions.LOAD_PLANNED_DELIVERIES({
              orderId: action.orderId,
              posId: action.posId
            })
          ]),
          tap(result => this.ls.setBlocking('deliveryNote', false, null)),
          catchError(err => {
            this.ls.setBlocking('deliveryNote', false, null);
            return of(DeliveriesActions.CREATE_DELIVERYNOTE_ERROR());
          })
        );
      })
    );
  });

  validatePbo$ = createEffect(() => {
    return this.actions.pipe(
      ofType(DeliveriesActions.VALIDATE_PBO_SUBTYPES),
      exhaustMap(action => {
        return this.supplierService.validatePboSubtypes(action.pboId, action.pseId).pipe(
          map(result => {
            return DeliveriesActions.VALIDATE_PBO_SUBTYPES_SUCCESS({
              pbos: result.result
            });
          })
        );
      })
    );
  });

  createDeliveryNoteSuccess$ = createEffect(() => {
    return this.actions.pipe(
      ofType(DeliveriesActions.CREATE_DELIVERYNOTE_SUCCESS),
      map(action => {
        this.notificationService.showNotification('Lieferschein erzeugt');
        return DeliveriesActions.DOWNLOAD_DELIVERYNOTE({noteId: action.deliveryNote.value});
      })
    );
  });

  downloadDeliveryNote$ = createEffect(() => {
    return this.actions.pipe(
      ofType(DeliveriesActions.DOWNLOAD_DELIVERYNOTE),
      exhaustMap(action => {
        return this.supplierService.getDeliveryNote(action.noteId).pipe(
          exhaustMap(deliveryNote => {
            return this.documentsService.getDocumentFile(deliveryNote.DE_DOC_ID).pipe(
              map(response => {
                const fileResponse: FileResponse = FileResponse.parseResponse(response);
                saveAs(fileResponse.content, fileResponse.fileName);
                return DeliveriesActions.DOWNLOAD_DELIVERYNOTE_SUCCESS();
              })
            );
          }),
          catchError(err => {
            this.notificationService.showError(err);
            return of(undefined);
          })
        );
      })
    );
  });

  loadDeliveryIcons$ = createEffect(() => {
    return this.actions.pipe(
      ofType(DeliveriesActions.LOAD_DELIVERY_ICONS),
      map(action => action.deliveries.map(delivery => delivery.FREIGABE_ICON)),
      exhaustMap(iconNames => {
        const joinedIconNames = iconNames.join(',');
        if (joinedIconNames.length > 0) {
          return this.imageService.getIconUrls(joinedIconNames).pipe(
            map(icons => DeliveriesActions.LOAD_DELIVERY_ICONS_SUCCESS({icons}))
          );
        }
      })
    );
  });

  loadDeliveryChart$ = createEffect(() => {
    return this.actions.pipe(
      ofType(DeliveriesActions.LOAD_DELIVERIES_CHART),
      exhaustMap(action => {
        return this.getAliasFields(action.orderId, action.posId).pipe(
          exhaustMap(position => {
            return this.supplierService.getPlannedDeliveries(action.orderId, action.posId).pipe(
              exhaustMap(deliveries => {
                const sortedDeliveries = this.sortDeliveries(deliveries);
                return forkJoin({
                  planned: this.analyticsService.executeReport(
                    this.guids.GUID_REPORT_PLANNED_DELIVERY_COUNT, this.getChartParams(position, sortedDeliveries)
                  ),
                  requested: this.analyticsService.executeReport(
                    this.guids.GUID_REPORT_REQUESTED_DELIVERY_COUNT, this.getChartParams(position, sortedDeliveries)
                  ),
                  estimated: this.analyticsService.executeReport(
                    this.guids.GUID_REPORT_ESTIMATED_DELIVERYCURVE, this.getChartParams(position, sortedDeliveries)
                  ),
                  actual: this.analyticsService.executeReport(
                    this.guids.GUID_REPORT_ACTUAL_DELIVERY_COUNT, this.getChartParams(position, sortedDeliveries)
                  )
                }).pipe(
                  map(result => {
                    return DeliveriesActions.LOAD_DELIVERIES_CHART_SUCCESS({
                      chartOptions: this.createChartOptions(result.planned, result.requested, result.estimated, result.actual)
                    });
                  })
                );
              })
            );
          })
        );
      })
    );
  });

  getChartParams(pos: ProleisObject, deliveries: ProleisObject[]) {
    let startDate = pos.DATE_START;
    let endDate = pos.DATE_END;

    if (deliveries.length > 0) {
      if (deliveries[0].PLANNED_DATE < startDate) {
        startDate = deliveries[0].PLANNED_DATE;
      }

      if (deliveries[deliveries.length - 1].PLANNED_DATE > endDate) {
        endDate = deliveries[deliveries.length - 1].PLANNED_DATE;
      }
    }
    return [
      {name: 'REPORT_PSE_ID', type: Types.DOUBLE, value: pos.OBJECT_ID},
      {name: 'REPORT_TIME_UNIT', type: Types.TEXT, value: 'WEEK'},
      {name: 'STARTDATE', type: Types.DATE, value: startDate},
      {name: 'ENDDATE', type: Types.DATE, value: endDate},
      {name: 'TIMEUNIT', type: Types.TEXT, value: 'WEEK'},
      {name: 'TOTAL_COUNT', type: Types.DOUBLE, value: Number(pos.STUELICOUNT)},
      {name: 'DATE_1', type: Types.DATE, value: pos.DATE_1},
      {name: 'DATE_2', type: Types.DATE, value: pos.DATE_2},
      {name: 'VALUE_1', type: Types.DOUBLE, value: Number(pos.VALUE_1)},
      {name: 'VALUE_2', type: Types.DOUBLE, value: Number(pos.VALUE_2)},
      {name: 'VALUE_STEPS', type: Types.DOUBLE, value: Number(pos.VALUE_STEPS)}
    ];

  }

  getAliasFields(orderId: string, posId: string): Observable<ProleisObject> {
    const aliasFields = 'DATE_1=OBJECT_ID.PARENT.MEILENSTEIN_CONT_ID.CHILD[NAME=A-TER].MEILENSTEIN_VALUE_TYP_ID.VALUE;' +
      'DATE_2=OBJECT_ID.PARENT.MEILENSTEIN_CONT_ID.CHILD[NAME=B-TER].MEILENSTEIN_VALUE_TYP_ID.VALUE;' +
      'VALUE_2=OBJECT_ID.PARENT.TOTAL_LIEFERSTAFFELUNG_WOCHE;' +
      'VALUE_1=OBJECT_ID.PARENT.TOTAL_LIEFERSTAFFELUNG_WOCHE;' +
      'VALUE_STEPS=OBJECT_ID.PARENT.TOTAL_LIEFERSTAFFELUNG_WOCHE;' +
      'DATE_START=$SYSDATE();' +
      'DATE_END=$DATEFUNC( $DATEADD("m",3,$SYSDATE()),MONTH_START)';
    return this.supplierService.getPosition(orderId, posId, aliasFields);
  }

  createChartOptions(planned: ProleisObject[], requested: ProleisObject[], estimated: ProleisObject[],
                     actual: ProleisObject[]): Options {
    const categories = planned.map(d => d.TIMELABEL);
    const options: Options = {
      title: {
        text: undefined
      },
      yAxis: {
        title: {
          text: 'Anzahl'
        }
      },
      xAxis: {
        title: {
          text: 'KW'
        },
        categories
      },
      legend: {
        layout: 'vertical',
        align: 'right',
        verticalAlign: 'middle'
      },
      series: [{
        name: 'Geplant',
        type: 'line',
        data: planned.map(d => Number(d.PLANNED_COUNT_SUM))
      }, {
        name: 'Stückzahl',
        type: 'line',
        data: requested.map(d => Number(d.VALUE))
      }, {
        name: 'Geschätzt',
        type: 'line',
        data: estimated.map(d => Number(d.VALUE))
      }, {
        name: 'Geliefert',
        type: 'line',
        data: actual.map(d => Number(d.COUNT_DELIVERED_SUM))
      }]
    };
    return options;
  }

  sortDeliveries(deliveries: ProleisObject[]) {
    return deliveries.sort((a, b) => {
      if (a.PLANNED_DATE > b.PLANNED_DATE) {
        return 1;
      } else if (a.PLANNED_DATE < b.PLANNED_DATE) {
        return -1;
      }
      return 0;
    });
  }

}
